/**
 * Busca todas as negociações da organização para o dashboard.
 * Independente do funil selecionado no Kanban (estado compartilhado do LeadsContext).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Lead } from '../../../types';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { getLeadsUseCase } from '../../../infrastructure/di/crmContainer';
import { mapDomainLeadToFrontend, noopLeadHistoryCache } from '../../../contexts/leads/utils/leadMappers';

export const useDashboardLeads = (enabled: boolean) => {
    const { currentOrganization } = useOrganization();
    const [leads, setLeads] = useState<Lead[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const requestIdRef = useRef(0);

    const refresh = useCallback(async () => {
        if (!enabled || !currentOrganization?.id) return;

        const orgId = currentOrganization.id;
        const requestId = ++requestIdRef.current;

        setIsLoading(true);
        try {
            const domainLeads = await getLeadsUseCase.execute(undefined, orgId);
            if (requestId !== requestIdRef.current) return;

            const mapped = domainLeads
                .filter(l => l.name && l.name.trim().length >= 2)
                .map(lead => mapDomainLeadToFrontend(lead, noopLeadHistoryCache));

            setLeads(mapped);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error('[useDashboardLeads] Failed to load org leads:', error);
            setLeads([]);
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [enabled, currentOrganization?.id]);

    useEffect(() => {
        if (!enabled || !currentOrganization?.id) {
            setLeads([]);
            setIsLoading(false);
            return;
        }

        void refresh();
    }, [enabled, currentOrganization?.id, refresh]);

    useEffect(() => {
        if (!enabled) return;

        const onAssignmentChange = () => {
            void refresh();
        };

        window.addEventListener('assignment:changed', onAssignmentChange);
        return () => window.removeEventListener('assignment:changed', onAssignmentChange);
    }, [enabled, refresh]);

    return { leads, isLoading };
};
